import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import css from './CopyLinkBox.module.css';

/**
 * WLE — shows a shareable URL next to a copy button.
 *
 * The button copies `caption` when one is given, so the clipboard carries the
 * ready-to-post wording rather than a bare link, while the box still shows the
 * URL the caption ends with.
 *
 * `navigator.clipboard` is undefined on insecure origins and in older Safari,
 * so the missing-API case is handled the same way as a rejected write: the
 * button says so instead of throwing.
 *
 * @param {Object} props
 * @param {string} props.url the URL to display and share
 * @param {string} [props.caption] full text to copy; defaults to the URL
 * @param {string} [props.className] wrapper class, for spacing at the call site
 * @param {string} [props.buttonLabel] idle button label
 */
const RESET_DELAY_MS = 1000;

const CopyLinkBox = props => {
  const { url, caption, className, buttonLabel = 'Copy Link' } = props;
  const [status, setStatus] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!url) {
    return null;
  }

  const showStatus = text => {
    setStatus(text);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setStatus(null), RESET_DELAY_MS);
  };

  const handleCopy = () => {
    const textToCopy = caption || url;
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;

    if (!clipboard || typeof clipboard.writeText !== 'function') {
      showStatus('Copy Failed');
      return;
    }

    clipboard.writeText(textToCopy).then(
      () => showStatus('Copied!'),
      err => {
        console.error('Could not copy text: ', err);
        showStatus('Copy Failed');
      }
    );
  };

  return (
    <div className={classNames(css.container, className)}>
      <div className={css.urlBox}>{url}</div>
      <button className={css.copyButton} type="button" onClick={handleCopy}>
        {status || buttonLabel}
      </button>
    </div>
  );
};

export default CopyLinkBox;
